import React, { useState, useRef } from 'react';
import { Modal, Select } from '../../../../components';
import { useMutate, useAppAndBranchId, usePermissions } from '../../../../hooks';
import { api } from '../../api';
import { useToast } from '../../../../../../../../helpers/commonFunction';
import style from '../add-media.module.scss';
import ImgIcon from '../../../../../../../../assets/svg/solo-chat/add-image.svg';
import EyeIcon from '../../../../../../../../assets/svg/solo-chat/image-eye.svg';
import DeleteIcon from '../../../../../../../../assets/svg/solo-chat/image-delete.svg';
import { Icon, Spin } from 'antd';
import { isUploadedFromStudio, isValidHttpUrl } from '../../utils';
import { ContentMode } from '../../../../types';
import { validateList } from '../../../../utils/validation';
import { errorMessage } from '../../../../utils/error';
import { allowedContentTypes, maxSize } from '../../../../constants';
import { readFileAsync } from '../../../../utils/readFileAsync';
import { getRandomId } from '../../../../../../../../utils/random-id';

type Image = {
	fileName: string;
	description: string;
	url: string;
};

type Props = {
	isOpen: boolean;
	close: () => void;
	mode?: ContentMode;
	initialValue?: Image & { id: string };
	refetchContent: () => void;
	onCreate?: (contentId: string) => void;
	isPreview?: boolean;
};

export function AddImage(props: Props) {
  const canDelete = usePermissions('content.Delete')
	const inputRef = useRef<HTMLInputElement>(null);
	const { ErrorToast, WarningToast } = useToast();
	const [appId, branchId] = useAppAndBranchId();
	const {
		isOpen,
		close,
		initialValue,
		refetchContent,
		onCreate,
		isPreview = false,
	} = props;
	const [state, setState] = useState<Image>(
		initialValue ?? {
			fileName: '',
			description: '',
			url: '',
		},
	);
	const [mode, setMode] = useState(props.mode ?? 'create');
	const [type, setType] = useState<'upload' | 'url'>('upload');

  const [preview, setPreview] = useState("")
  const [file, setFile] = useState<File | null>(null)
  const [deleteFile, setDeleteFile] = useState('')
  const [uploadDeleteLoading, setUploadDeleteLoading] = useState(false)

	const { mutate: createMutate, loading: createLoading } = useMutate(
		api.createContent,
	);
	const { mutate: updateMutate, loading: updateLoading } = useMutate(
		api.updateContent,
	);
	const { mutate: uploadFile, loading: uploadLoading } = useMutate(
		api.uploadFile,
	);

	const [error, setError] = useState({
		description: '',
		url: '',
	});

  function clearError(key: keyof typeof error) {
	  setError(prev => ({...prev, [key]: ""}))
	}

	function validateImage() {
		const result = validateList([
			{
				name: 'url',
				value: preview.replace("blob:", "") || state.url,
				rules: [
					{
						message: "Upload an image or provide a valid URL",
						fn: (value: string) => value.trim().length <= 0,
					},
					{
						message: "Upload an image or provide a valid URL",
						fn: (value: string) => !isValidHttpUrl(value),
					},
				],
			},
			{
				name: 'description',
				value: state.description,
				rules: [
					{
						message: "Description can't be empty",
						fn: (value: string) => value.trim().length <= 0,
					},
				],
			},
		]);
		const hasError = Object.keys(result).length !== 0
		if(hasError) {
		  setError(prev => ({ ...prev, ...result }))
		}
		return !hasError
	}

	async function onFilePick(e: React.ChangeEvent<HTMLInputElement>) {
		clearError("url")
		if (e.target.files?.length) {
			const file = e.target.files[0];
      if(!allowedContentTypes.image.includes(file.type)) {
        WarningToast("Please select a valid image")
        return
      }
      if(file.size > maxSize.image) {
        WarningToast("Please select a image less than 5MB")
        return
      }
      const preview = await readFileAsync(file)
      setPreview(preview)
      setFile(file)
      if (inputRef.current) {
        inputRef.current.value = '';
			}
		}
	}

	const selectStyles: Record<string, string> = {
		'--select-top': '6px',
		'--select-options-top': '23px',
		'--select-right': type === 'url' ? '28px' : '40px',
		'--select-chat-border': 'none',
		'--select-space': '0px',
		'--select-width': '150px',
	};

	function getFilename(url: string) {
		const segments = url.split('/');
		const filename = segments[segments.length - 1].split('.')[0];
    return filename || getRandomId()
	}

	return (
		<Modal
			hideFooter={isPreview}
			loading={createLoading || updateLoading || uploadDeleteLoading}
			isOpen={isOpen}
			title={
				mode === 'edit'
					? 'Update Image'
					: mode === 'create'
					? 'Add Image'
					: 'View Image'
			}
			okText={mode === 'view' ? 'Edit' : mode === 'create' ?  'Submit' : 'Update'}
			onCancel={() => {
				setState({
					fileName: '',
					url: '',
					description: '',
				});
				close();
			}}
			onOk={async () => {
				if (mode !== 'view' && !validateImage()) {
					return;
				}
				const payload: Record<string, any> = {
					branchId,
					contentType: 'image',
					contentValue: {
						fileName: state.fileName,
						url: state.url,
						description: state.description,
					},
				};
        setUploadDeleteLoading(true)
        if(deleteFile) {
          await api.deleteFile(appId, deleteFile);
          setDeleteFile("")
        }
        if(file) {
          const url = await uploadFile(appId, file, file.type);
          setPreview("")
          setFile(null)
          setState(prev => ({
            ...prev,
            url,
            fileName: file.name
          }))
          payload.contentValue.url = url
          payload.contentValue.fileName = file.name
        }
				if (payload.contentValue.url && !payload.contentValue.fileName) {
					payload.contentValue.fileName = getFilename(payload.contentValue.url);
				}
				try {
					if (mode === 'edit') {
						await updateMutate(appId, initialValue?.id, payload);
					} else if (mode === 'create') {
						const res = await createMutate(appId, payload);
						if (onCreate) {
							onCreate(res.contentId);
						}
					} else {
						setMode('edit');
						return;
					}
					refetchContent();
					close();
				} catch (err) {
					ErrorToast(errorMessage(mode, 'image', err.code));
				} finally {
          setUploadDeleteLoading(false)
        }
			}}
		>
			<div className={style.addMedia}>
				<div className={style.select} style={selectStyles}>
					<Select
						options={options}
						placeholder="Select"
						value={type}
						onSelect={(value: any) => setType(value)}
					/>
				</div>

				{type == 'upload' && (
					<div className={style.upload}>
						<label htmlFor="picker">Image</label>
						<input
							onChange={onFilePick}
							accept="image/*"
							ref={inputRef}
							id="picker"
							type="file"
						/>
						{!state.url && !preview && (
							<button
								onClick={() => {
									if (inputRef.current) {
										inputRef.current.click();
									}
								}}
								className={style.frame}
							>
								{uploadLoading ? (
									<Spin
										indicator={
											<Icon type="loading" style={{ fontSize: 24 }} spin />
										}
									/>
								) : (
									<img src={ImgIcon} alt="add image" />
								)}
							</button>
						)}

						{(state.url || preview) && (
							<div className={style.frame} data-media="image">
								<img src={preview || state.url} alt="preview" />
								<div className={style.actions}>
									<button
										onClick={() => {
											window.open(preview || state.url, '_blank');
										}}
									>
										<img src={EyeIcon} alt="view" />
									</button>
									{mode !== 'view' && canDelete && (
										<button
											onClick={async () => {
												if (inputRef.current) {
                          setPreview('')
                          setFile(null)
													setState((prev) => ({
														...prev,
														url: '',
														fileName: '',
													}));
													inputRef.current.value = '';
													if (isUploadedFromStudio(state.url)) {
                            setDeleteFile(state.url)
													}
												}
											}}
										>
											<img src={DeleteIcon} alt="delete" />
										</button>
									)}
								</div>
							</div>
						)}
						<span className={style.error}>{error.url}</span>
					</div>
				)}

				{type === 'url' && (
					<div className={style.url}>
						<label htmlFor="url">URL</label>
						<input
							disabled={mode === 'view'}
							id="url"
							value={state.url}
							placeholder="Enter url"
							onChange={(e) => {
                setFile(null)
                setPreview('')
								clearError("url")
								const value = e.currentTarget.value;
								setState((prev) => ({ ...prev, url: value }));
							}}
						/>
						<span className={style.error}>{error.url}</span>
					</div>
				)}

				<div className={style.desc}>
					<div className={style.header}>
						<label htmlFor="desc">Description</label>
						<span data-empty={state.description.trim() === ''}>
							{state.description.trim().length} / 250
						</span>
					</div>
					<textarea
						disabled={mode === 'view'}
						id="desc"
						value={state.description}
						placeholder="Enter description"
						onChange={(e) => {
							clearError("description")
							const value = e.currentTarget.value;
							if (value.length <= 250) {
								setState((prev) => ({ ...prev, description: value }));
							}
						}}
					/>
					<span className={style.error}>{error.description}</span>
				</div>
			</div>
		</Modal>
	);
}

const options = [
	{ label: 'Upload Image', value: 'upload' },
	{ label: 'Add Image URL', value: 'url' },
];
